// TripsList — Phase 12 Plan 12-03 (UIR-04, telegram-ui migration of DIS-16).
//
// Wave 1 "first impression" screen for owner/dispatcher. Migrated from the
// Phase 4 plain-DOM version to telegram-ui per UI-SPEC §7 / §R5:
//   - native <TabsList> (Active / Completed / Problems) instead of role="tab";
//   - <List>/<Section> + TripCard on <Cell> (R1/R2 patterns live in TripCard);
//   - <Placeholder> empty states (copywriting UI-SPEC §8);
//   - <Skeleton> while loading; <Banner type="warning"> on load error;
//   - bottom bar: native Telegram MainButton "+ New trip" (primary CTA,
//     double-tap-safe + cleaned up on unmount — Pitfall 5 / T-12-03-01) plus a
//     secondary telegram-ui <Button> "Import Excel".
//
// PRESERVED (do NOT regress): react-query useInfiniteQuery data-fetch, the
// IntersectionObserver infinite-scroll sentinel, search + filter chips, and all
// data-testid attributes that downstream tests / E2E rely on.

import { useInfiniteQuery } from "@tanstack/react-query";
import { mainButton } from "@telegram-apps/sdk-react";
import {
  Banner,
  Button,
  List,
  Placeholder,
  Section,
  Skeleton,
  TabsList,
} from "@telegram-apps/telegram-ui";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { type TripListItem, type TripListResponse, tripsApi } from "../api/trips";
import { useAuth } from "../auth/useAuth";
import { TripCard } from "../components/TripCard";
import { SAFE_AREA_BOTTOM } from "../lib/safeArea";
import { TRIP_STATUS_COLORS } from "../lib/tokens";
import { tripStatusFill } from "../lib/tripStatusBadge";

type TabKey = "active" | "completed" | "problems";

const TAB_KEYS: readonly TabKey[] = ["active", "completed", "problems"];
const PAGE_SIZE = 20;

// R1: problems map to the red trip-status color (TRIP_STATUS_COLORS) — kept as a
// named constant so the style-bridge to the shared token is explicit.
const PROBLEM_COLOR_KEY = "red" as const;

export function TripsList(): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("active");
  const [search, setSearch] = useState<string>("");

  const trimmedSearch = search.trim();

  const query = useInfiniteQuery<TripListResponse, Error>({
    queryKey: ["trips-list", tab, trimmedSearch],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      tripsApi.list(apiClient, {
        status: tab,
        offset: typeof pageParam === "number" ? pageParam : 0,
        limit: PAGE_SIZE,
        search: trimmedSearch === "" ? undefined : trimmedSearch,
      }),
    getNextPageParam: (lastPage) => {
      const fetched = lastPage.offset + lastPage.items.length;
      return fetched < lastPage.total ? fetched : undefined;
    },
  });

  // IntersectionObserver-based "load more" sentinel (infinite scroll — preserved).
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!query.hasNextPage || query.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void query.fetchNextPage();
        }
      },
      { rootMargin: "100px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage, query]);

  // Native Telegram MainButton — primary CTA "+ New trip". Mount once, set
  // params, wire a single navigate-only click (double-tap is harmless: it just
  // re-navigates), and clean up on unmount so the button never "sticks" onto the
  // next screen (Pitfall 5 / threat T-12-03-01). Guarded so non-Telegram envs
  // (jsdom/dev outside WebView) degrade gracefully.
  useEffect(() => {
    const label = t("dispatcher:trips.new_cta", "+ New trip") as string;
    const onClick = (): void => {
      void navigate("/dispatcher/trips/new");
    };
    let removeClick: (() => void) | undefined;
    try {
      if (typeof mainButton.mount === "function") {
        mainButton.mount();
      }
      if (typeof mainButton.setParams === "function") {
        mainButton.setParams({ text: label, isVisible: true, isEnabled: true });
      }
      if (typeof mainButton.onClick === "function") {
        const maybeRemover = mainButton.onClick(onClick);
        if (typeof maybeRemover === "function") {
          removeClick = maybeRemover;
        }
      }
    } catch {
      // Not running inside Telegram WebView — secondary on-screen CTA still works.
    }
    return () => {
      try {
        // Prefer the disposer returned by onClick; fall back to offClick.
        if (removeClick) {
          removeClick();
        } else if (typeof mainButton.offClick === "function") {
          mainButton.offClick(onClick);
        }
        if (typeof mainButton.setParams === "function") {
          mainButton.setParams({ isVisible: false });
        }
        if (typeof mainButton.unmount === "function") {
          mainButton.unmount();
        }
      } catch {
        // ignore cleanup errors outside Telegram WebView
      }
    };
  }, [t, navigate]);

  const allItems: TripListItem[] = (query.data?.pages ?? []).flatMap((p) => p.items);

  function emptyCopy(): { header: string; description: string } {
    if (trimmedSearch.length > 0) {
      return {
        header: t("dispatcher:trips.empty.search.heading", "No matching trips") as string,
        description: t(
          "dispatcher:trips.empty.body",
          "Create your first trip or import from Excel.",
        ) as string,
      };
    }
    if (tab === "completed") {
      return {
        header: t("dispatcher:trips.empty.completed.heading", "No completed trips") as string,
        description: t(
          "dispatcher:trips.empty.body",
          "Create your first trip or import from Excel.",
        ) as string,
      };
    }
    if (tab === "problems") {
      return {
        header: t("dispatcher:trips.empty.problems.heading", "No problems — all clear") as string,
        description: t("dispatcher:trips.empty.problems.body", "Everything is on track.") as string,
      };
    }
    return {
      header: t("dispatcher:trips.empty.active.heading", "No active trips") as string,
      description: t(
        "dispatcher:trips.empty.body",
        "Create your first trip or import from Excel.",
      ) as string,
    };
  }

  const empty = emptyCopy();
  const showEmpty = !query.isLoading && !query.isError && allItems.length === 0;

  return (
    <div data-testid="trips-list" style={{ paddingBottom: 96 }}>
      <List>
        <Section
          header={t("dispatcher:trips.title", "Trips") as string}
          data-testid="trips-list-section"
        >
          <div style={{ padding: "0 16px" }}>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                t("dispatcher:trips.search.placeholder", "Search by trip ID or driver") as string
              }
              data-testid="trips-search"
              style={{
                width: "100%",
                padding: 10,
                margin: "8px 0 12px",
                border: "1px solid var(--tgui--section_separator_color, #e2e8f0)",
                borderRadius: 8,
                fontSize: 14,
                background: "var(--tgui--bg_color, transparent)",
                color: "var(--tgui--text_color, inherit)",
                boxSizing: "border-box",
              }}
            />

            <TabsList data-testid="trips-tabs">
              {TAB_KEYS.map((k) => (
                <TabsList.Item
                  key={k}
                  selected={tab === k}
                  data-testid={`trips-tab-${k}`}
                  aria-selected={tab === k}
                  onClick={() => setTab(k)}
                >
                  {t(`dispatcher:trips.tab.${k}`, k) as string}
                  {/* R1 style-bridge: the Problems tab carries the SAME red the
                      TripCard status badge uses (single trip-color everywhere),
                      via TRIP_STATUS_COLORS — not a hardcoded hex. */}
                  {k === "problems" && PROBLEM_COLOR_KEY in TRIP_STATUS_COLORS && (
                    <span
                      data-testid="trips-tab-problems-indicator"
                      aria-hidden="true"
                      style={{
                        display: "inline-block",
                        width: 6,
                        height: 6,
                        marginLeft: 6,
                        borderRadius: 3,
                        verticalAlign: "middle",
                        backgroundColor: tripStatusFill(PROBLEM_COLOR_KEY),
                      }}
                    />
                  )}
                </TabsList.Item>
              ))}
            </TabsList>

            <div
              data-testid="trips-filter-chips"
              style={{ display: "flex", gap: 6, margin: "12px 0", flexWrap: "wrap", fontSize: 12 }}
            >
              <span data-testid="trips-filter-driver" style={chipStyle}>
                {t("dispatcher:trips.filter.driver", "All drivers") as string} ▾
              </span>
              <span data-testid="trips-filter-date" style={chipStyle}>
                {t("dispatcher:trips.filter.date", "Any date") as string} ▾
              </span>
              <span data-testid="trips-filter-more" style={chipStyle}>
                {t("dispatcher:trips.filter.more", "More") as string} ▾
              </span>
            </div>
          </div>

          {query.isLoading && (
            <div data-testid="trips-list-loading" style={{ padding: "0 16px" }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} visible>
                  <div style={{ height: 56, marginBottom: 6, borderRadius: 8 }} />
                </Skeleton>
              ))}
            </div>
          )}

          {query.isError && (
            <div style={{ padding: "0 16px" }}>
              <Banner
                type="inline"
                data-testid="trips-list-error"
                header={t("dispatcher:trips.error.load", "Failed to load trips.") as string}
                description={
                  t(
                    "dispatcher:trips.error.load_hint",
                    "Check your connection and pull down to refresh.",
                  ) as string
                }
              />
            </div>
          )}

          {showEmpty && (
            <Placeholder
              data-testid="trips-list-empty"
              header={empty.header}
              description={empty.description}
            />
          )}

          <div data-testid="trips-list-items">
            {allItems.map((trip) => (
              <TripCard key={trip.id} trip={trip} />
            ))}
          </div>

          <div ref={sentinelRef} data-testid="trips-list-sentinel" style={{ height: 1 }} />

          {query.isFetchingNextPage && (
            <div data-testid="trips-list-loading-more" style={{ padding: "0 16px" }}>
              <Skeleton visible>
                <div style={{ height: 56, borderRadius: 8 }} />
              </Skeleton>
            </div>
          )}
        </Section>
      </List>

      {/* Secondary CTA — "Import Excel". The primary CTA ("+ New trip") is the
          native Telegram MainButton wired in the effect above. */}
      <div
        data-testid="trips-list-cta-bar"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 64,
          padding: "8px 16px",
          paddingBottom: SAFE_AREA_BOTTOM,
          background: "var(--tgui--bg_color, var(--tg-theme-bg-color, #fff))",
          borderTop:
            "1px solid var(--tgui--section_separator_color, var(--tg-theme-section-separator-color, #e2e8f0))",
        }}
      >
        <Button
          mode="bezeled"
          size="m"
          stretched
          data-testid="trips-list-import-cta"
          onClick={() => navigate("/dispatcher/trips/import")}
        >
          {t("dispatcher:trips.import_cta", "+ Import Excel") as string}
        </Button>
      </div>
    </div>
  );
}

const chipStyle: React.CSSProperties = {
  padding: "4px 10px",
  border: "1px solid var(--tgui--section_separator_color, #e2e8f0)",
  borderRadius: 999,
  color: "var(--tgui--hint_color, #64748B)",
};
